import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useCreateSurvey, useUpdateSurvey } from '../hooks/useSurveys';
import { useSurveyTemplatesByTenant } from '../../../features/survey-template/hooks/useSurveyTemplates';
import { SurveyFormData, SurveyTableData } from '../types';
import { CreateSurveyDTO, UpdateSurveyDTO } from '../dtos';

interface AddSurveyDialogProps {
  open: boolean;
  onClose: () => void;
  editingItem?: SurveyTableData | null;
  onSuccess?: () => void;
  subjectId: string;
  tenantId: string;
  domainId: string;
}

export const AddSurveyDialog: React.FC<AddSurveyDialogProps> = ({
  open,
  onClose,
  editingItem,
  onSuccess,
  subjectId,
  tenantId,
  domainId,
}) => {
  const isEditing = !!editingItem;
  const createSurveyMutation = useCreateSurvey(subjectId!);
  const updateSurveyMutation = useUpdateSurvey(subjectId!);

  // Fetch survey templates for the tenant (only when creating, not editing)
  const {
    data: templatesResponse,
    isLoading: isLoadingTemplates,
    error: templatesError,
  } = useSurveyTemplatesByTenant(tenantId);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<SurveyFormData>({
    mode: 'onChange',
    defaultValues: {
      title: editingItem?.title || '',
      description: editingItem?.description || '',
      status: editingItem?.status || 'DRAFT',
      templateId: editingItem?.templateId || '',
    },
  });

  // Reset form when dialog opens/closes or editing item changes
  React.useEffect(() => {
    if (open) {
      reset({
        title: editingItem?.title || '',
        description: editingItem?.description || '',
        status: editingItem?.status || 'DRAFT',
        templateId: editingItem?.templateId || '',
      });
    }
  }, [open, editingItem, reset]);

  const onSubmit = async (data: SurveyFormData) => {
    try {
      if (isEditing && editingItem) {
        const updateData: UpdateSurveyDTO = {
          title: data.title,
          description: data.description,
          status: data.status,
        };
        await updateSurveyMutation.mutateAsync({
          surveyId: editingItem.id,
          data: updateData,
        });
      } else {
        const createData: CreateSurveyDTO = {
          subjectId,
          tenantId,
          domainId,
          title: data.title,
          description: data.description,
          status: data.status || 'DRAFT',
          templateId: data.templateId || undefined,
        };
        await createSurveyMutation.mutateAsync(createData);
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      // Error handling is done in the mutation hooks
      console.error('Survey operation failed:', error);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const isLoading = createSurveyMutation.isPending || updateSurveyMutation.isPending;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditing ? 'Edit Survey' : 'Create New Survey'}</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Controller
              name="title"
              control={control}
              rules={{
                required: 'Survey title is required',
                minLength: {
                  value: 2,
                  message: 'Title must be at least 2 characters',
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Survey Title"
                  variant="outlined"
                  fullWidth
                  error={!!errors.title}
                  helperText={errors.title?.message}
                  disabled={isLoading}
                />
              )}
            />

            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Description"
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={3}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  disabled={isLoading}
                />
              )}
            />

            {/* Template selection - only show when creating new surveys */}
            {!isEditing && (
              <Controller
                name="templateId"
                control={control}
                render={({ field }) => (
                  <FormControl
                    fullWidth
                    error={!!errors.templateId}
                    disabled={isLoading || isLoadingTemplates}
                  >
                    <InputLabel>Template (Optional)</InputLabel>
                    <Select {...field} label="Template (Optional)">
                      <MenuItem value="">
                        <em>No Template</em>
                      </MenuItem>
                      {templatesResponse?.data?.templates
                        ?.filter((template) => template.status === 'ACTIVE')
                        ?.map((template) => (
                          <MenuItem key={template.id} value={template.id}>
                            {template.title}
                          </MenuItem>
                        ))}
                    </Select>
                    {templatesError && (
                      <Box sx={{ mt: 1, fontSize: '0.75rem', color: 'error.main' }}>
                        Error loading templates
                      </Box>
                    )}
                    {isLoadingTemplates && (
                      <Box sx={{ mt: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
                        Loading templates...
                      </Box>
                    )}
                  </FormControl>
                )}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!isValid || isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
